import { PermissionCategoryDto } from '@/api/generated';
import {
  userRolesOptionsTranslation,
  UserRolesOptionsTranslation,
} from '@/assets/locales/utils/dictionaries/dictionaries.translation.pl.ts';

export type AdministrationTranslation = {
  categories: Record<PermissionCategoryDto, string>;
  description: string;
  loadError: string;
  maintenance: {
    adminsAnd: string;
    adminsOnly: string;
    createdBy: string;
    current: {
      active: string;
      cancel: string;
      finish: string;
      planned: string;
    };
    errors: {
      endBeforeStart: string;
      missingDates: string;
      startInPast: string;
    };
    form: {
      allowed: string;
      allowedHint: string;
      announce: string;
      announceHint: string;
      cancel: string;
      confirm: string;
      confirmNow: string;
      duration: string;
      end: string;
      message: string;
      messageHint: string;
      now: string;
      planned: string;
      schedule: string;
      start: string;
      switchOn: string;
      title: string;
      when: string;
    };
    history: string;
    intro: string;
    status: Record<'ACTIVE' | 'CANCELLED' | 'FINISHED' | 'PLANNED', string>;
  };
  roleNames: UserRolesOptionsTranslation;
  roles: {
    adminColumn: string;
    cancel: string;
    changedRoles: string;
    fixedForEveryone: string;
    fixedForRoles: string;
    hint: string;
    leaveDialog: {
      content: string;
      leave: string;
      stay: string;
      title: string;
    };
    morePeople: string;
    permission: string;
    save: string;
    showPeople: string;
    usersCount: string;
  };
  tabs: {
    maintenance: string;
    roles: string;
    users: string;
  };
  title: string;
  users: {
    accessDialog: {
      active: string;
      activeLabel: string;
      adminHasAll: string;
      cancel: string;
      extraHint: string;
      extraLabel: string;
      fromRole: string;
      inactive: string;
      noRole: string;
      roleLabel: string;
      save: string;
      selfHint: string;
      title: string;
    };
    active: string;
    allRoles: string;
    awaitingApproval: string;
    edit: string;
    email: string;
    empty: string;
    emptyRoles: string;
    extraPermissions: string;
    filters: {
      all: string;
      awaiting: string;
      inactive: string;
      search: string;
      status: string;
    };
    groupCount: string;
    inactive: string;
    name: string;
    nobody: string;
    status: string;
    withoutRole: string;
  };
};

export const administrationTranslation: AdministrationTranslation = {
  categories: {
    [PermissionCategoryDto.Function]: 'Funkcje',
    [PermissionCategoryDto.Menu]: 'Menu',
  },
  description:
    'Co widzi każda rola, kto ma jaką rolę i jakie dodatkowe uprawnienia mają pojedyncze osoby. Zmiany widać u użytkownika po odświeżeniu strony.',
  loadError: 'Nie udało się wczytać danych administracji.',
  maintenance: {
    adminsAnd: 'Pracować mogą: administratorzy oraz',
    adminsOnly: 'Pracować mogą tylko administratorzy',
    createdBy: 'Ustawione przez: {{name}}',
    current: {
      active: 'Tryb serwisowy jest włączony do {{end}}',
      cancel: 'Anuluj przerwę',
      finish: 'Zakończ teraz',
      planned: 'Zaplanowana przerwa: {{range}}',
    },
    errors: {
      endBeforeStart: 'Koniec przerwy musi być po jej początku',
      missingDates: 'Podaj początek i koniec przerwy',
      startInPast: 'Początek zaplanowanej przerwy musi być w przyszłości',
    },
    form: {
      allowed: 'Kto może dalej pracować',
      allowedHint:
        'Administratorzy mogą pracować zawsze. Dopisz np. osobę, która sprawdzi aplikację po wdrożeniu.',
      announce: 'Wyślij ogłoszenie do wszystkich',
      announceHint: 'Trafi do Ogłoszeń: na Pulpit i na pasek w całej aplikacji.',
      cancel: 'Nie teraz',
      confirm: 'Tak, włącz teraz',
      confirmNow:
        'Wszyscy poza administratorami i wskazanymi osobami zostaną od razu odcięci i zobaczą ekran przerwy technicznej. Na pewno?',
      duration: 'Na ile',
      end: 'Koniec',
      message: 'Komunikat dla użytkowników (opcjonalnie)',
      messageHint: 'Pokaże się na ekranie przerwy, np. „Wdrażamy nową wersję”.',
      now: 'Teraz',
      planned: 'Zaplanuj',
      schedule: 'Zaplanuj przerwę',
      start: 'Początek',
      switchOn: 'Włącz tryb serwisowy',
      title: 'Włącz tryb serwisowy',
      when: 'Kiedy',
    },
    history: 'Poprzednie przerwy',
    intro:
      'W trybie serwisowym aplikacja jest niedostępna dla wszystkich poza administratorami i osobami, które tu wskażesz. Pozostali widzą ekran przerwy technicznej z godziną powrotu, a strona sama się odświeży po zakończeniu.',
    status: {
      ACTIVE: 'Trwa',
      CANCELLED: 'Anulowana',
      FINISHED: 'Zakończona',
      PLANNED: 'Zaplanowana',
    },
  },
  roleNames: userRolesOptionsTranslation,
  roles: {
    adminColumn: 'Administrator ma zawsze wszystkie uprawnienia',
    cancel: 'Odrzuć zmiany',
    changedRoles: 'Zmienione role: {{roles}}',
    fixedForEveryone: 'Tę pozycję menu widzi każdy. Nie da się jej odebrać.',
    fixedForRoles: 'Tę pozycję menu widzi tylko: {{roles}}. Nie da się jej nadać innym rolom.',
    hint: 'Zaznacz, co ma widzieć i robić każda rola. Kolumny administratora nie da się zmienić.',
    leaveDialog: {
      content: 'Zmiany w uprawnieniach ról nie zostały zapisane. Wyjść bez zapisu?',
      leave: 'Wyjdź bez zapisu',
      stay: 'Zostań',
      title: 'Niezapisane zmiany',
    },
    morePeople: 'i jeszcze {{count}} os.',
    permission: 'Uprawnienie',
    save: 'Zapisz zmiany',
    showPeople: 'Pokaż osoby z rolą {{role}}',
    usersCount: '{{count}} os.',
  },
  tabs: {
    maintenance: 'Tryb serwisowy',
    roles: 'Co widzą role',
    users: 'Kto ma jaką rolę',
  },
  title: 'Administracja',
  users: {
    accessDialog: {
      active: 'Aktywne',
      activeLabel: 'Konto',
      adminHasAll: 'Administrator ma wszystkie uprawnienia — dodatkowe nie są potrzebne.',
      cancel: 'Anuluj',
      extraHint: 'Dokładane do tego, co daje rola. Uprawnień z roli nie da się tu odebrać.',
      extraLabel: 'Dodatkowe uprawnienia',
      fromRole: 'Z roli: {{permissions}}',
      inactive: 'Nieaktywne',
      noRole: 'Wybierz rolę, żeby nadać dostęp.',
      roleLabel: 'Rola',
      save: 'Zapisz',
      selfHint: 'Własnej roli i aktywności konta nie zmienisz.',
      title: 'Dostęp: {{name}}',
    },
    active: 'Aktywne',
    allRoles: 'Wszystkie role',
    awaitingApproval: 'Czeka na akceptację',
    edit: 'Zmień dostęp',
    email: 'E-mail',
    empty: 'Brak kont spełniających filtry.',
    emptyRoles: 'Nikt nie ma ról: {{roles}}',
    extraPermissions: 'Dodatkowe uprawnienia',
    filters: {
      all: 'Wszystkie',
      awaiting: 'Czeka na akceptację',
      inactive: 'Nieaktywne',
      search: 'Szukaj po imieniu, nazwisku lub e-mailu',
      status: 'Status',
    },
    groupCount: '{{count}} os.',
    inactive: 'Nieaktywne',
    name: 'Imię i nazwisko',
    nobody: 'Nikt nie ma tej roli',
    status: 'Konto',
    withoutRole: 'Bez roli — czekają na akceptację',
  },
};
